import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';
import { setupActivityTracking, initializeSecurity, roleChecks, SESSION_TIMEOUT_MINUTES, DEMO_SESSION_TIMEOUT_MINUTES } from './security';
import { getToken, getStoredUser, setStoredUser, setRealSession, clearSession } from './session';
import i18n from './i18n';

const AuthContext = createContext(null);

// P_MORNING_BRIEF — User Language Contract.
// The user OWNS their language. Priority chain at session boot/login:
//   1. user.preferred_language (server-persisted)
//   2. organizations.default_language (server-persisted on the org doc)
//   3. localStorage.i18nextLng (i18next-browser-languagedetector cache)
//   4. "en" (final fallback configured in lib/i18n.js)
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es'];
const SUPPORTED_THEMES = ['light', 'dark'];
const THEME_STORAGE_KEY = 'yulontap-theme-preference';

const applyUserLanguage = (userData) => {
    if (!userData) return;
    const candidates = [
        userData.preferred_language,
        userData.organization?.default_language,
        userData.organization_default_language,
    ];
    for (const lang of candidates) {
        if (typeof lang === 'string' && SUPPORTED_LANGUAGES.includes(lang)) {
            if (i18n.language !== lang) i18n.changeLanguage(lang);
            return;
        }
    }
    // No server-side preference → defer to i18next's own detection
    // chain (localStorage → navigator → 'en' fallback).
};

// P_THEME_PERSISTENCE_1.0 — User Theme Contract.
// Mirrors `applyUserLanguage`. When the user has a server-persisted
// `preferred_theme` (set via /auth/me/theme), apply it to the document
// and mirror it into localStorage so the ThemeProvider that already
// reads localStorage on mount stays consistent. This is the
// authoritative restore path on session boot and after login —
// localStorage alone is insufficient on iPad Safari (ITP wipes it
// after 7 days).
const applyUserTheme = (userData) => {
    if (!userData) return;
    const theme = userData.preferred_theme;
    if (typeof theme !== 'string' || !SUPPORTED_THEMES.includes(theme)) return;
    try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (_e) {
        // localStorage unavailable (private mode); fall back to DOM only.
    }
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [securityConfig, setSecurityConfig] = useState(null);

    // Initialize security measures
    useEffect(() => {
        initializeSecurity();
    }, []);

    // Showroom demo manager tab must always render dark/night, regardless of
    // any prior light-mode preference in this browser. Scoped to the demo tab.
    useEffect(() => {
        try {
            if (window.sessionStorage && window.sessionStorage.getItem('yulontap_demo_mode') === '1') {
                localStorage.setItem(THEME_STORAGE_KEY, 'dark');
                document.documentElement.classList.add('dark');
            }
        } catch (_e) { /* ignore */ }
    }, []);

    const loadUser = useCallback(async () => {
        const token = getToken();
        const savedUser = getStoredUser();
        
        if (token && savedUser) {
            try {
                // Verify token is still valid
                const response = await authAPI.getMe();
                setUser(response.data);
                setSecurityConfig(response.data.security_config || null);
                setIsAuthenticated(true);
                // P_MORNING_BRIEF — restore user's owned language on
                // session bootstrap (page reload, new tab, etc.).
                applyUserLanguage(response.data);
                // P_THEME_PERSISTENCE_1.0 — restore user's owned
                // light/dark choice on session bootstrap. Authoritative
                // path: server > localStorage > light default.
                applyUserTheme(response.data);
            } catch (error) {
                // Token invalid or session expired, clear storage
                clearSession();
                setUser(null);
                setSecurityConfig(null);
                setIsAuthenticated(false);
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    // Session timeout handling. The Demo Setting (super admin only) swaps the
    // idle window to 60 minutes; flipping it off puts 15 minutes back
    // immediately — no reload needed, because this effect re-installs the
    // tracker whenever the flag changes.
    const demoLongSession = user?.role === 'super_admin' && !!user?.demo_long_session;
    useEffect(() => {
        if (isAuthenticated) {
            const cleanup = setupActivityTracking(() => {
                // Session timed out
                logout();
                window.location.href = '/login?timeout=true';
            }, demoLongSession ? DEMO_SESSION_TIMEOUT_MINUTES : SESSION_TIMEOUT_MINUTES);
            return cleanup;
        }
    }, [isAuthenticated, demoLongSession]);

    // While the Demo Setting is ON, re-check the server every 5 minutes so the
    // nightly midnight-Eastern auto-off lands in an already-open window: the
    // pill disappears and the idle window drops back to 15 minutes.
    useEffect(() => {
        if (!isAuthenticated || !demoLongSession) return;
        const id = setInterval(async () => {
            try {
                const res = await authAPI.getMe();
                if (!res.data?.demo_long_session) {
                    setUser((prev) => (prev ? { ...prev, demo_long_session: false } : prev));
                }
            } catch (_e) { /* interceptor handles auth failures */ }
        }, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [isAuthenticated, demoLongSession]);

    const login = async (email, password) => {
        const response = await authAPI.login(email, password);
        const { access_token, user: userData } = response.data;
        
        setRealSession(access_token, userData);
        // v4.33.0 — Clear any prior user's active-org selection so we
        // never leak scope across logins. New session starts in default
        // mode (the axios interceptor omits the header when this key
        // is absent → backend uses the full assigned-org aggregate).
        localStorage.removeItem('yulontap_active_org');
        
        setUser(userData);
        setSecurityConfig(userData.security_config || null);
        setIsAuthenticated(true);
        // P_MORNING_BRIEF — restore user's owned language at login.
        applyUserLanguage(userData);
        // P_THEME_PERSISTENCE_1.0 — restore user's owned theme at login.
        applyUserTheme(userData);

        return userData;
    };

    const logout = async () => {
        // Call backend logout to invalidate session
        try {
            await authAPI.logout();
        } catch (e) {
            // Ignore errors - we're logging out anyway
        }
        
        clearSession();
        // v4.33.0 — Also clear the multi-org context selection.
        localStorage.removeItem('yulontap_active_org');
        setUser(null);
        setSecurityConfig(null);
        setIsAuthenticated(false);
    };

    const changePassword = async (currentPassword, newPassword) => {
        return await authAPI.changePassword(currentPassword, newPassword);
    };

    const isSuperAdmin = () => user?.role === 'super_admin' && user?.partner_access !== true;
    const isClientAdmin = () => user?.role === 'client_admin';
    const isLocationManager = () => user?.role === 'location_manager';
    const isEmployee = () => user?.role === 'employee' || user?.role === 'staff';
    const isFranchiseAdmin = () => user?.role === 'franchise_admin';
    const isStaff = () => user?.role === 'staff';

    // PARTNER: a super_admin record carrying partner_access. Everything they may
    // see or do comes from the ticked permissions, so the blanket super-admin
    // "yes" below must not apply to them.
    const isPartner = () => user?.partner_access === true;

    // ── Feature Permission System (default-deny, visibility-based) ──
    // Super Admin always has full access. All others use feature_permissions from backend.
    const hasPermission = (key) => {
        if (user?.partner_access === true) return user?.feature_permissions?.[key] === true;
        if (user?.role === 'super_admin') return true;
        return user?.feature_permissions?.[key] === true;
    };

    // SECURITY: Permission helpers using security_config from backend
    const canManageOrganizations = () => hasPermission('manage_organizations');
    const canManageLocations = () => hasPermission('manage_locations') || hasPermission('view_locations');
    const canManageEmployees = () => hasPermission('manage_employees');
    const canAssignCards = () => hasPermission('assign_staff');
    const canChangePlatform = () => hasPermission('review_destination');
    const canEditCardUrl = () => isSuperAdmin();
    const canEditBaselines = () => isSuperAdmin();
    const canEditBrands = () => isSuperAdmin();
    const canManageUsers = () => isSuperAdmin();
    const canChangeCardOrganization = () => isSuperAdmin();
    const canChangeCardLocation = () => hasPermission('manage_locations') || isSuperAdmin();
    
    // View permissions
    const canViewAnalytics = () => hasPermission('view_analytics');
    const canViewExecutiveSummary = () => hasPermission('view_executive_summary');
    const canViewBrandComparison = () => hasPermission('view_brand_comparison');
    const canViewLocationHealth = () => hasPermission('view_location_health');
    const canViewCards = () => true; // Cards page always visible (content filtered by permissions)
    const canViewAuditLogs = () => isSuperAdmin();
    
    // Export permissions
    const canExportFull = () => isSuperAdmin();
    const canExportSummary = () => hasPermission('view_analytics');
    
    // SECURITY: Show internal IDs only for Super Admin
    const canSeeInternalIds = () => isSuperAdmin();
    
    // Franchise Admin is view-only - cannot edit anything
    const isViewOnly = () => isFranchiseAdmin() && !hasPermission('manage_vouchers');
    
    // Ads feature permission - user-level
    const canUseAdsFeature = () => hasPermission('card_adverts');

    const value = {
        user,
        loading,
        isAuthenticated,
        securityConfig,
        login,
        logout,
        changePassword,
        // P_MORNING_BRIEF — server-persisted language toggle. The
        // browser is no longer the source of truth.
        setPreferredLanguage: async (lang) => {
            await authAPI.updateLanguage(lang);
            i18n.changeLanguage(lang);
            const next = { ...(user || {}), preferred_language: lang };
            setUser(next);
            setStoredUser(next);
            return lang;
        },
        // P_THEME_PERSISTENCE_1.0 — server-persisted theme toggle.
        // Called by ThemeContext.toggleTheme so the user's dark-mode
        // choice survives ITP wipes, new browsers, and new devices.
        setPreferredTheme: async (theme) => {
            if (!SUPPORTED_THEMES.includes(theme)) return null;
            // Best-effort server persistence — unlike language we
            // still apply the theme locally even if the server call
            // fails (no auth, offline) so the user is never stuck.
            try {
                await authAPI.updateTheme(theme);
                const next = { ...(user || {}), preferred_theme: theme };
                setUser(next);
                setStoredUser(next);
            } catch (_err) {
                // Local-only fallback. ThemeContext has already
                // mirrored the choice into THEME_STORAGE_KEY.
            }
            return theme;
        },
        // Personal preference patch (My Settings) — keeps the shell in
        // sync with the saved preference without a full /auth/me reload.
        patchUser: (fields) => {
            const next = { ...(user || {}), ...fields };
            setUser(next);
            setStoredUser(next);
            return next;
        },
        isSuperAdmin,
        isPartner,
        isClientAdmin,
        isLocationManager,
        isEmployee,
        // Demo Setting (super admin only).
        demoLongSession,
        setDemoLongSession: async (enabled) => {
            const res = await authAPI.updateDemoSession(!!enabled);
            const next = { ...(user || {}), demo_long_session: !!res.data?.demo_long_session };
            setUser(next);
            setStoredUser(next);
            return next.demo_long_session;
        },
        isFranchiseAdmin,
        isStaff,
        hasPermission,
        canManageOrganizations,
        canManageLocations,
        canManageEmployees,
        canAssignCards,
        canChangePlatform,
        canEditCardUrl,
        canEditBaselines,
        canEditBrands,
        canManageUsers,
        canChangeCardOrganization,
        canChangeCardLocation,
        canViewAnalytics,
        canViewExecutiveSummary,
        canViewBrandComparison,
        canViewLocationHealth,
        canViewCards,
        canViewAuditLogs,
        canExportFull,
        canExportSummary,
        canSeeInternalIds,
        isViewOnly,
        canUseAdsFeature,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
